import React, { PureComponent } from "react"; // eslint-disable-line no-unused-vars
import { Field, reduxForm, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";

import { Input, Radio, Grid, Col, CheckMark, Section } from "../../components";
import { messages, constants } from "../../constants";

import { validationBuilder, validations } from "../../containers/form";
import { Txt, Button, Title } from "../../elements";

import { validateEmail } from "../../util";

import infractionDisputeImage from "../../assets/img/parking.png";
import "./index.scss";

const MAX_FILE_SIZE = 1024 * 1024; // 1 MB
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".txt", ".docx"];
const ACCEPTED_FILE_TYPES = ALLOWED_EXTENSIONS.join(",");

const settings = {
  form: constants.FORM.infractionDisputeForm,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnChange: true,
};

class InfractionDispute extends PureComponent {
  state = {
    clientSideValidationError: null,
    resetSuccess: false,
    selectedFiles: [],
    fileError: null,
  };

  constructor(props) {
    super(props);

    const rules = Object.fromEntries(
      Object.entries(validations).map(([key, validation]) => [
        key,
        (spec, overrideErrorMessage = false) => (value) => {
          return validationBuilder(props, validation, spec, overrideErrorMessage)(value);
        },
      ])
    );
    const { intl } = props;

    this.ruleFirstName = [
      rules.required(intl.formatMessage(messages.infractionDispute.firstName)),
      rules.names(intl.formatMessage(messages.error.firstNameFormat), true),
    ];
    this.ruleLastName = [
      rules.required(intl.formatMessage(messages.infractionDispute.lastName)),
      rules.names(intl.formatMessage(messages.error.lastNameFormat), true),
    ];
    this.rulesEmail = [
      rules.required(intl.formatMessage(messages.infractionDispute.email)),
      rules.email(),
    ];
    this.rulePhone = [
      rules.required(intl.formatMessage(messages.infractionDispute.phoneNumber)),
      rules.phone(),
    ];
    this.ruleTicketNumber = [
      rules.required(intl.formatMessage(messages.infractionDispute.ticketNumber)),
    ];
    this.ruleDisputeReason = [
      rules.required(intl.formatMessage(messages.infractionDispute.disputeReason)),
    ];
    this.ruleOtherReason = [
      rules.required(intl.formatMessage(messages.infractionDispute.otherReasonPlaceholder)),
    ];
  }

  handleFileChange = (e) => {
    const { intl } = this.props;
    const newFiles = Array.from(e.target.files);

    if (newFiles.length === 0) {
      return;
    }

    const merged = [...this.state.selectedFiles, ...newFiles];

    if (merged.length > 2) {
      this.setState({
        fileError: intl.formatMessage(messages.infractionDispute.fileUploadTooMany),
      });
      e.target.value = "";
      return;
    }

    const invalidType = newFiles.find(f => {
      const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
      return !ALLOWED_EXTENSIONS.includes(ext);
    });
    if (invalidType) {
      this.setState({
        fileError: intl.formatMessage(messages.infractionDispute.fileUploadInvalidFormat),
      });
      e.target.value = "";
      return;
    }

    const oversized = newFiles.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) {
      this.setState({
        fileError: intl.formatMessage(messages.infractionDispute.fileUploadTooLarge),
      });
      e.target.value = "";
      return;
    }

    this.setState({ selectedFiles: merged, fileError: null });
    e.target.value = "";
  };

  removeFile = (index) => {
    this.setState((prevState) => ({
      selectedFiles: prevState.selectedFiles.filter((_, i) => i !== index),
      fileError: null,
    }));
  };

  sendEmail = async (payload) => {
    const { intl } = this.props;
    if (!validateEmail(payload.email)) {
      this.setState({ clientSideValidationError: intl.formatMessage(messages.error.email) });
      return null;
    }

    const { selectedFiles } = this.state;
    const formData = new FormData();
    const payloadData = { ...payload };
    if (selectedFiles.length > 0) {
      payloadData.useAttachedFiles = true;
    }
    formData.append("type", constants.EMAIL_TYPE.INFRACTION_DISPUTE);
    formData.append("payload", JSON.stringify(payloadData));
    selectedFiles.forEach((file) => {
      formData.append("file", file);
    });

    try {
      const resp = await fetch(constants.ENDPOINTS.sendEmail, {
        method: "POST",
        body: formData,
      });

      if (resp.status !== 200) {
        this.setState({
          clientSideValidationError: intl.formatMessage(messages.infractionDispute.errorGeneric),
          resetSuccess: false,
        });
      } else {
        this.setState({
          clientSideValidationError: null,
          resetSuccess: true,
        });
      }
      return resp;
    } catch (error) {
      this.setState({
        clientSideValidationError: error.message,
        resetSuccess: false,
      });
    }
  };

  renderSuccessfulScreen = () => {
    const { intl } = this.props;

    return (
      <div className="reset-code" data-testid="infraction-dispute-success" id="infraction-dispute-success">
        <Grid theme={["center"]}>
          <Col size="1-2" min="sm">
            <CheckMark />
            <Title
              priority={1}
              type={["strong"]}
              style={{ padding: "50px" }}
              className="h1 reset-code__title blue"
            >
              {intl.formatMessage(messages.infractionDispute.successTitle)}
            </Title>
            <Title priority={2} className="h2 reset-code__title">
              {intl.formatMessage(messages.infractionDispute.successMessage)}
            </Title>
          </Col>
        </Grid>
      </div>
    );
  };

  render() {
    const { submitting, invalid, pristine, intl, handleSubmit, selectedDisputeReason } = this.props;
    const { clientSideValidationError, resetSuccess, selectedFiles, fileError } = this.state;

    if (resetSuccess) {
      return this.renderSuccessfulScreen();
    }

    return (
      <div className="outerForm">
        <div className="infraction-dispute__header">
          <img
            src={infractionDisputeImage}
            alt={intl.formatMessage(messages.infractionDispute.headerAlt)}
          />
          <span className="infraction-dispute__header-overlay">PARKING</span>
        </div>
        <Title priority={2} className="infraction-dispute__title">
          {intl.formatMessage(messages.infractionDispute.title)}
        </Title>
        <div style={{ padding: "30px 0" }}>
          <form
            onSubmit={handleSubmit(this.sendEmail)}
            noValidate
          >
            <Txt theme={[`middle`, `large`]}>
              {intl.formatMessage(messages.infractionDispute.description)}
            </Txt>
            <Section size="2-3" min="md">
              <Grid>
                <Col size="2-3" min="sm">
                  <Field
                    type="text"
                    name="firstName"
                    label={intl.formatMessage(messages.infractionDispute.firstName)}
                    required={true}
                    component={Input}
                    validate={this.ruleFirstName}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="2-3" min="sm">
                  <Field
                    type="text"
                    name="lastName"
                    label={intl.formatMessage(messages.infractionDispute.lastName)}
                    required={true}
                    component={Input}
                    validate={this.ruleLastName}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="2-3" min="sm">
                  <Field
                    type="email"
                    name="email"
                    label={intl.formatMessage(messages.infractionDispute.email)}
                    required={true}
                    validate={this.rulesEmail}
                    component={Input}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="2-3" min="sm">
                  <Field
                    type="tel"
                    name="phoneNumber"
                    label={intl.formatMessage(messages.infractionDispute.phoneNumber)}
                    required={true}
                    component={Input}
                    validate={this.rulePhone}
                    props={{ maxLength: "20" }}
                  />
                </Col>

                <Col size="2-3" min="sm">
                  <Field
                    type="text"
                    name="ticketNumber"
                    label={intl.formatMessage(messages.infractionDispute.ticketNumber)}
                    required={true}
                    component={Input}
                    validate={this.ruleTicketNumber}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="2-3" min="sm">
                  <Field
                    type="text"
                    name="licensePlate"
                    label={intl.formatMessage(messages.infractionDispute.licensePlate)}
                    required={false}
                    component={Input}
                    props={{ maxLength: "20" }}
                  />
                </Col>

                <Col size="2-3" min="sm">
                  <Field
                    name="disputeReason"
                    label={intl.formatMessage(messages.infractionDispute.disputeReason)}
                    required={true}
                    component={Radio}
                    validate={this.ruleDisputeReason}
                    theme={["vertical"]}
                    labelClassName="infraction-dispute__section-label"
                    options={[
                      {
                        label: intl.formatMessage(messages.infractionDispute.disputeReasonParked),
                        value: "Parked in a dedicated Evo parking spot",
                      },
                      {
                        label: intl.formatMessage(messages.infractionDispute.disputeReasonPaid),
                        value: "Paid for parking",
                      },
                      {
                        label: intl.formatMessage(messages.infractionDispute.disputeReasonEmergency),
                        value: "Had an emergency/vehicle breakdown",
                      },
                      {
                        label: intl.formatMessage(messages.infractionDispute.disputeReasonOther),
                        value: "Other",
                      },
                    ]}
                  />
                  {selectedDisputeReason === "Other" && (
                    <Field
                      type="text"
                      name="otherReason"
                      label={intl.formatMessage(messages.infractionDispute.otherReasonPlaceholder)}
                      required={true}
                      component={Input}
                      validate={this.ruleOtherReason}
                      props={{ maxLength: "100" }}
                    />
                  )}
                </Col>

                <Col size="1-1" min="sm">
                  <Field
                    name="additionalInfo"
                    label={intl.formatMessage(messages.infractionDispute.additionalInfo)}
                    required={false}
                    component="textarea"
                    maxLength="1000"
                    rows="5"
                    className="infraction-dispute__textarea"
                    placeholder={intl.formatMessage(messages.infractionDispute.additionalInfo)}
                  />
                </Col>

                <Col size="1-1" min="sm">
                  <fieldset className="infraction-dispute__file-upload" role="group">
                    <Txt theme={["large"]}>
                      <strong>{intl.formatMessage(messages.infractionDispute.fileUploadLabel)}</strong>
                    </Txt>
                    <Txt theme={["large"]}>
                      {intl.formatMessage(messages.infractionDispute.fileUploadConstraints)}
                    </Txt>
                    <input
                      ref={(ref) => { this.fileInput = ref; }}
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      multiple
                      onChange={this.handleFileChange}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      onClick={() => this.fileInput.click()}
                      className="btn btn--cta"
                      disabled={selectedFiles.length >= 2 || submitting}
                    >
                      <span>{intl.formatMessage(messages.infractionDispute.chooseFiles)}</span>
                    </button>
                    {selectedFiles.length > 0 && (
                      <div>
                        {selectedFiles.map((f, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                            <span style={{ fontSize: "inherit" }}>{f.name}</span>
                            <button
                              type="button"
                              onClick={() => this.removeFile(i)}
                              style={{
                                marginLeft: "8px",
                                border: "none",
                                background: "none",
                                color: "#c00",
                                cursor: "pointer",
                                fontSize: "inherit",
                                fontWeight: "bold",
                                lineHeight: 1,
                                padding: 0,
                              }}
                              aria-label={`Remove ${f.name}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {fileError && (
                      <Txt theme={["large", "error"]}>
                        {fileError}
                      </Txt>
                    )}
                  </fieldset>
                </Col>
              </Grid>
            </Section>
            <br />
            <Txt theme={["small"]}>
              {intl.formatMessage(messages.infractionDispute.requiredFieldLegend)}
            </Txt>
            <br />
            {clientSideValidationError && (
              <Txt theme={["error"]} data-testid="infraction-dispute-error" id="infraction-dispute-error">
                <small>{clientSideValidationError}</small>
              </Txt>
            )}
            <Button
              loading={submitting}
              type="submit"
              theme={["cta"]}
              disabled={invalid || submitting || pristine}
            >
              {intl.formatMessage(messages.infractionDispute.submit)}
            </Button>
          </form>
        </div>
      </div>
    );
  }
}

InfractionDispute.propTypes = {
  intl: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  invalid: PropTypes.bool,
  submitting: PropTypes.bool,
  pristine: PropTypes.bool,
  selectedDisputeReason: PropTypes.string,
};

InfractionDispute.defaultProps = {
  submitting: false,
  invalid: false,
  pristine: true,
  selectedDisputeReason: null,
};

const mapStateToProps = (state) => {
  const selector = formValueSelector(constants.FORM.infractionDisputeForm);
  return {
    selectedDisputeReason: selector(state, "disputeReason"),
  };
};

const enhancer = compose(injectIntl, connect(mapStateToProps), reduxForm(settings));

export default enhancer(InfractionDispute);
